// HTTP calls for the Owner's close of the Lead Agent pause notice of the chat
// (AGI-766, MLA-100). bff-web proxies both to leadbilling, which owns the close.

import { AxiosService } from 'src/axios/axios.service';

/** Why the Owner doesn't want the Lead Agent. A stable contract with billing and analytics. */
export type tLeadAgentChatBannerDismissReason =
  | 'too_expensive'
  | 'not_needed'
  | 'unclear_value'
  | 'did_not_work'
  | 'other_tool'
  | 'other';

/** The reasons, in the order the question lists them. */
export const LEAD_AGENT_CHAT_BANNER_DISMISS_REASONS: readonly tLeadAgentChatBannerDismissReason[] = [
  'too_expensive',
  'not_needed',
  'unclear_value',
  'did_not_work',
  'other_tool',
  'other',
];

/** The answer sent with the close. Every field is optional: null means skipped or unknown. */
export type tLeadAgentChatBannerDismissBody = {
  reason: tLeadAgentChatBannerDismissReason | null;
  reasonText: string | null;
  everUsed: boolean | null;
};

/** The open close, as billing stores it. It may be an earlier one: the first close wins. */
export type tLeadAgentChatBannerDismissal = {
  dismissedAt: string | null;
  pauseReason: string | null;
  reason: string | null;
  everUsed: boolean | null;
  daysSinceTrialEnd: number | null;
};

type tChatBannerEnvelope = {
  data?: { dismissed?: boolean };
};

type tChatBannerDismissalEnvelope = {
  data?: {
    dismissedAt?: string | null;
    pauseReason?: string | null;
    reason?: string | null;
    everUsed?: boolean | null;
    daysSinceTrialEnd?: number | null;
  };
};

export type tDismissLeadAgentChatBannerOptions = {
  workspaceId: string;
  body: tLeadAgentChatBannerDismissBody;
};

export const LeadAgentChatBannerService = {
  /**
   * GET /workspace/{wid}/lead-agent/chat-banner
   *
   * Whether the Owner closed the notice for the whole workspace. Any answer
   * other than `dismissed: true` reads as not closed.
   */
  getDismissed: async function (workspaceId: string, signal?: AbortSignal): Promise<boolean> {
    const res = await AxiosService.get<tChatBannerEnvelope>(
      `workspace/${workspaceId}/lead-agent/chat-banner`,
      { isBFFService: true, signal },
    );
    return res?.data?.dismissed === true;
  },

  /**
   * POST /workspace/{wid}/lead-agent/chat-banner/dismiss
   *
   * Closes the notice for every role and device, with the Owner's optional
   * answer. Billing answers 403 to anyone but the Owner and 409 outside a
   * commercial pause; both reject this promise.
   */
  dismiss: async function (options: tDismissLeadAgentChatBannerOptions): Promise<tLeadAgentChatBannerDismissal> {
    const res = await AxiosService.post<tLeadAgentChatBannerDismissBody, tChatBannerDismissalEnvelope>(
      `workspace/${options.workspaceId}/lead-agent/chat-banner/dismiss`,
      options.body,
      { isBFFService: true },
    );
    const data = res?.data;
    return {
      dismissedAt: data?.dismissedAt ?? null,
      pauseReason: data?.pauseReason ?? null,
      reason: data?.reason ?? null,
      everUsed: data?.everUsed ?? null,
      daysSinceTrialEnd: data?.daysSinceTrialEnd ?? null,
    };
  },
};
